/**
 * Utility for Google Click Identifier (GCLID) capture and persistence.
 * Captures ?gclid=... from URL query parameters (both search query and hash query)
 * and persists it in a 90-day first-party cookie with domain=.protraderscans.com
 * (for cross-domain sharing between protraderscans.com and app.protraderscans.com)
 * as well as localStorage fallback.
 */

const GCLID_COOKIE_KEY = 'gclid';
const GCLID_STORAGE_KEY = 'gclid';
const COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60; // 90 days

/**
 * Extracts gclid from the current URL (search or hash query string).
 * @returns {string|null}
 */
export function extractGclid() {
  if (typeof window === 'undefined') return null;

  try {
    // 1. Check standard URL search query (e.g. https://app.protraderscans.com/?gclid=xyz#/)
    const urlParams = new URLSearchParams(window.location.search);
    const gclidFromSearch = urlParams.get('gclid');
    if (gclidFromSearch) {
      return gclidFromSearch.trim();
    }

    // 2. Check hash route query string (e.g. https://app.protraderscans.com/#/?gclid=xyz)
    if (window.location.hash && window.location.hash.includes('?')) {
      const hashQuery = window.location.hash.split('?')[1];
      const hashParams = new URLSearchParams(hashQuery);
      const gclidFromHash = hashParams.get('gclid');
      if (gclidFromHash) {
        return gclidFromHash.trim();
      }
    }
  } catch (err) {
    console.warn('[GCLID] Error extracting gclid from URL:', err);
  }

  return null;
}

/**
 * Persists gclid in both a 90-day cookie and localStorage.
 * Uses domain=.protraderscans.com in production so the marketing site and app share it.
 * @param {string} gclid
 */
export function storeGclid(gclid) {
  if (typeof window === 'undefined' || !gclid) return;

  try {
    const isProTraderDomain = window.location.hostname.includes('protraderscans.com');
    const domainAttr = isProTraderDomain ? '; domain=.protraderscans.com' : '';
    const secureAttr = window.location.protocol === 'https:' ? '; Secure' : '';

    // Set 90-day first-party cookie
    document.cookie = `${GCLID_COOKIE_KEY}=${encodeURIComponent(gclid)}; max-age=${COOKIE_MAX_AGE_SECONDS}; path=/; SameSite=Lax${domainAttr}${secureAttr}`;

    // Set localStorage fallback
    localStorage.setItem(GCLID_STORAGE_KEY, gclid);
  } catch (err) {
    console.warn('[GCLID] Error storing gclid:', err);
  }
}

/**
 * Retrieves the stored gclid from cookie or localStorage fallback.
 * @returns {string|null}
 */
export function getStoredGclid() {
  if (typeof window === 'undefined') return null;

  try {
    // 1. Try reading from cookie
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.startsWith(`${GCLID_COOKIE_KEY}=`)) {
        const val = cookie.substring(GCLID_COOKIE_KEY.length + 1);
        if (val) {
          return decodeURIComponent(val);
        }
      }
    }

    // 2. Try reading from localStorage fallback
    const storedVal = localStorage.getItem(GCLID_STORAGE_KEY);
    if (storedVal) {
      return storedVal;
    }
  } catch (err) {
    console.warn('[GCLID] Error reading stored gclid:', err);
  }

  return null;
}

/**
 * Checks URL for incoming gclid on page load and stores it if present.
 */
export function initGclidCapture() {
  const gclid = extractGclid();
  if (gclid) {
    storeGclid(gclid);
  }
}
